/**
 * MeetingNotesTab - Displays meeting notes captured from meeting transcripts from Google Meet and Zoom.
 */

import { useQuery } from '@tanstack/react-query';
import { File06, RefreshCw01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { useRefreshAt } from '@/hooks/useRefreshAt';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { useMeetingNoteOwners } from '@/lib/crm/hooks/useMeetingNoteOwners';
import { MEETING_NOTES_TO_OWNER_ASSOCIATION } from '@/lib/crm/utils/meetingNotes';

import { createCRMObjectClient } from '../../core/CRMObjectClient';
import { MeetingNotesDetail } from './MeetingNotesDetail';

interface MeetingNotesTabProps {
  /** Organization ID */
  orgId: number;
  /** Entity ID of the meeting */
  meetingEntityId: string;
  /** ISO 8601 start time of the meeting (used for contextual empty state) */
  meetingStartTime?: string;
}

/**
 * Loading skeleton for meeting notes.
 */
function LoadingSkeleton() {
  return (
    <div className='space-y-3'>
      {[1, 2].map(i => (
        <div
          key={i}
          className='p-4 rounded-lg animate-pulse border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)]'
        >
          <div className='h-4 rounded w-2/3 mb-2 bg-[var(--color-bg-tertiary)]' />
          <div className='h-3 rounded w-1/4 mb-3 bg-[var(--color-bg-tertiary)]' />
          <div className='h-3 rounded w-3/4 mb-1.5 bg-[var(--color-bg-tertiary)]' />
          <div className='h-3 rounded w-2/3 bg-[var(--color-bg-tertiary)]' />
        </div>
      ))}
    </div>
  );
}

/**
 * Error state component.
 */
function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className='flex flex-col items-center justify-center py-8 text-center'>
      <p className='text-xs mb-3 text-[var(--color-text-error-primary)]'>
        We couldn&apos;t load meeting summaries
      </p>
      <Button
        color='secondary'
        size='sm'
        onClick={onRetry}
        iconLeading={RefreshCw01}
      >
        Retry
      </Button>
    </div>
  );
}

/**
 * Meeting Notes tab component.
 */
export function MeetingNotesTab({
  orgId,
  meetingEntityId,
  meetingStartTime,
}: MeetingNotesTabProps) {
  // Refresh when the meeting starts so the empty state text updates
  // from "No meeting summary captured yet" to "No meeting summary captured".
  useRefreshAt(meetingStartTime ? new Date(meetingStartTime).getTime() : null);

  const fetchMeetingNotes = useCallback(async () => {
    const client = createCRMObjectClient(orgId, 'ahoy_meeting_notes');
    const response = await client.list(
      {
        associated_with_entity_id: meetingEntityId,
        properties: [
          'ahoy_title',
          'ahoy_notes_source',
          'ahoy_summary',
          'ahoy_action_items',
          'ahoy_key_topics',
          'ahoy_created_at',
          'ahoy_speaker_map',
          'ahoy_transcript_file_id',
        ],
        order_by: '-ahoy_created_at',
      },
      {
        include_associations: {
          [MEETING_NOTES_TO_OWNER_ASSOCIATION]: { properties: [] },
        },
      }
    );
    return response.results;
  }, [orgId, meetingEntityId]);

  // Fetch meeting notes associated with this meeting
  const {
    data: meetingNotes,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['meetingNotes', orgId, meetingEntityId],
    queryFn: fetchMeetingNotes,
    enabled: !!meetingEntityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const notesForOwners = useMemo(() => meetingNotes ?? [], [meetingNotes]);
  const ownerByNote = useMeetingNoteOwners(notesForOwners);

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return <ErrorState onRetry={refetch} />;
  }

  // Empty state — contextual text based on whether the meeting has passed
  if (!meetingNotes || meetingNotes.length === 0) {
    const isMeetingPast = meetingStartTime
      ? new Date(meetingStartTime) < new Date()
      : false;
    const emptyTitle = isMeetingPast
      ? 'No meeting summary captured'
      : 'No meeting summary captured yet';
    const emptyDescription = isMeetingPast
      ? 'No transcript was available for this meeting.'
      : 'Meeting summaries will appear here automatically when transcripts are available.';

    return (
      <EmptyState
        variant='empty'
        title={emptyTitle}
        description={emptyDescription}
        icon={
          <FeaturedIcon
            icon={File06}
            size='md'
            theme='light-outline'
            color='gray'
          />
        }
        size='sm'
      />
    );
  }

  // Display meeting notes
  return (
    <div className='space-y-3'>
      {meetingNotes.map(note => (
        <MeetingNotesDetail
          key={note.entity_id}
          note={note}
          orgId={orgId}
          meetingStartTime={meetingStartTime}
          ownerEntityId={ownerByNote.get(note.entity_id)}
        />
      ))}
    </div>
  );
}
